import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LinkAsButton } from "@/components/LinkAsButton";
import { EntityListPage } from "@/components/entity-page/EntityListPage";
import { getFormOptions } from "@/lib/api";
import type { FormOption } from "@/types";

export const Route = createFileRoute("/dashboard/settings/languages/")({
  component: LanguagesIndexPage,
});

function LanguagesIndexPage() {
  return <LanguagesIndexPageComponent />;
}

// Exported for testing
export function LanguagesIndexPageComponent() {
  const [languages, setLanguages] = useState<FormOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getFormOptions("languages")
      .then((options) => {
        if (!active) return;
        setLanguages(options);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching languages:", err);
        setError("Failed to load languages.");
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <EntityListPage
      title="Languages"
      description="The speech languages/locales agents can use during calls - each option here populates a voice agent's language field."
      items={languages}
      loading={loading}
      error={error}
      getItemKey={(language) => language.id}
      gridClassName="grid grid-cols-1 gap-4"
      renderItem={(language) => (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle>{language.label}</CardTitle>
                <CardDescription>{language.description || `Value: ${language.value}`}</CardDescription>
                <p className="text-xs text-muted-foreground mt-1">Order: {language.order ?? 0}</p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/dashboard/settings/languages/$id" params={{ id: language.id }}>
                  Edit
                </Link>
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}
      createHref="/dashboard/settings/languages/new"
      createLabel="Create Language"
      emptyMessage="No languages yet. Create one to get started."
      headerActions={
        <LinkAsButton to="/dashboard/settings" variant="ghost" size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Settings
        </LinkAsButton>
      }
    />
  );
}
